"use client";

import type { CourseCard } from "@/lib/types/course";

type Props = {
    /** When set (e.g. user came from My Courses), show real title / counts while modules load. */
    listPreview?: CourseCard | null;
};

export default function CourseDetailSkeleton({ listPreview }: Props) {
    const metaLine = listPreview
        ? [
              listPreview.moduleCount > 0 ? `${listPreview.moduleCount} modules` : null,
              listPreview.lessons > 0 ? `${listPreview.lessons} lessons` : null,
          ]
              .filter(Boolean)
              .join(" · ")
        : null;

    return (
        <div className="bg-gray-50 min-h-screen pb-20 animate-in fade-in duration-200">
            <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                    <div className="flex flex-col gap-4 sm:gap-6">
                        <div>
                            {listPreview ? (
                                <>
                                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2 pr-8">
                                        {listPreview.title}
                                    </h1>
                                    <div className="flex flex-wrap items-center gap-2 mb-3 text-sm text-slate-600">
                                        {listPreview.track ? (
                                            <span className="font-medium text-slate-700">{listPreview.track}</span>
                                        ) : null}
                                        {listPreview.organizationName ? (
                                            <span className="rounded-md bg-indigo-50 text-indigo-800 px-2 py-0.5 text-xs font-semibold">
                                                {listPreview.organizationName}
                                            </span>
                                        ) : null}
                                    </div>
                                    {metaLine ? (
                                        <p className="text-sm text-slate-500 mb-3">{metaLine}</p>
                                    ) : (
                                        <div className="h-4 w-56 bg-gray-100 rounded mb-3 animate-pulse" />
                                    )}
                                    {!listPreview.completed && listPreview.progress > 0 ? (
                                        <div className="flex items-center gap-3 sm:gap-4 mb-1">
                                            <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                                <div
                                                    className="bg-indigo-600 h-2.5 rounded-full transition-all"
                                                    style={{ width: `${listPreview.progress}%` }}
                                                />
                                            </div>
                                            <span className="text-xs font-semibold text-indigo-700 shrink-0">
                                                {listPreview.progress}%
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3 sm:gap-4">
                                            <div className="flex-1 bg-gray-100 rounded-full h-2.5 animate-pulse" />
                                            <div className="h-4 w-16 bg-gray-100 rounded animate-pulse" />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="h-8 sm:h-9 w-3/4 max-w-xl bg-gray-200 rounded-lg mb-2 animate-pulse" />
                                    <div className="h-4 w-48 bg-gray-100 rounded mb-4 animate-pulse" />
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        <div className="flex-1 bg-gray-100 rounded-full h-2.5 animate-pulse" />
                                        <div className="h-4 w-16 bg-gray-100 rounded animate-pulse" />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center gap-3 pb-2 pt-2 border-b border-gray-200">
                        <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
                        <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
                    </div>

                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="flex items-center justify-between p-5">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-gray-100 animate-pulse" />
                                    <div className="space-y-2">
                                        <div className="h-4 w-48 sm:w-64 bg-gray-200 rounded animate-pulse" />
                                        <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
                                    </div>
                                </div>
                                <div className="w-5 h-5 rounded bg-gray-100 animate-pulse" />
                            </div>
                            <div className="border-t border-gray-100 px-5 py-3 space-y-2">
                                {[1, 2, 3].map((j) => (
                                    <div key={j} className="flex items-center gap-3">
                                        <div className="w-5 h-5 rounded bg-gray-100 animate-pulse shrink-0" />
                                        <div className="h-3 flex-1 max-w-[60%] bg-gray-100 rounded animate-pulse" />
                                        <div className="h-3 w-12 bg-gray-100 rounded animate-pulse" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="space-y-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="h-5 w-32 bg-gray-200 rounded mb-4 animate-pulse" />
                        <div className="space-y-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gray-100 animate-pulse shrink-0" />
                                    <div className="h-4 flex-1 bg-gray-100 rounded animate-pulse" />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="h-4 w-24 bg-gray-200 rounded mb-4 animate-pulse" />
                        <div className="h-20 bg-gray-100 rounded animate-pulse" />
                    </div>
                </div>
            </div>
        </div>
    );
}
